import { useEffect, useState, type FormEvent } from 'react';

import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingRows } from '@/components/shared/LoadingRows';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatMinorAmount } from '@/lib/commerce-format';
import {
  type ReportDescriptor,
  type ReportRunResult,
  type RunReportBody,
  useExportReportMutation,
  useGetReportDescriptorsQuery,
  useRunReportMutation,
} from '@/store/api/commerceP2cApi';

function reportParams(descriptor: ReportDescriptor, values: Record<string, string>): NonNullable<RunReportBody['params']> {
  const params: NonNullable<RunReportBody['params']> = {};
  for (const field of descriptor.params) {
    const value = values[field.name]?.trim();
    if (!value) continue;
    if (field.type === 'date') {
      const date = new Date(`${value}T00:00:00`);
      if (!Number.isNaN(date.getTime())) params[field.name] = date.toISOString();
    } else {
      params[field.name] = value;
    }
  }
  return params;
}

export function ReportsPage() {
  const { data: reports, isLoading, isError, refetch } = useGetReportDescriptorsQuery();
  const [runReport, { isLoading: isRunning }] = useRunReportMutation();
  const [exportReport, { isLoading: isExporting }] = useExportReportMutation();
  const [reportId, setReportId] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [rows, setRows] = useState<Array<Record<string, unknown>>>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [runParams, setRunParams] = useState<RunReportBody['params']>();
  const [runError, setRunError] = useState(false);
  const [dateRangeError, setDateRangeError] = useState(false);
  const [result, setResult] = useState<ReportRunResult>();
  const selectedReport = reports?.find((report) => report.id === reportId);

  useEffect(() => {
    if (!reports?.length) return;
    if (!reports.some((report) => report.id === reportId)) setReportId(reports[0].id);
  }, [reports, reportId]);

  useEffect(() => {
    setValues({});
    setRows([]);
    setNextCursor(null);
    setRunParams(undefined);
    setResult(undefined);
    setRunError(false);
  }, [reportId]);

  async function execute(params: RunReportBody['params'], cursor?: string) {
    if (!reportId) return;
    setRunError(false);
    try {
      const page = await runReport({
        reportId,
        body: { params: params ?? {}, cursor, limit: 100 },
      }).unwrap();
      setRows((previous) => cursor ? [...previous, ...page.rows] : page.rows);
      setNextCursor(page.nextCursor);
      setRunParams(params ?? {});
      setResult(page);
    } catch {
      setRunError(true);
    }
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedReport) return;
    setDateRangeError(false);
    const params = reportParams(selectedReport, values);
    if (params.dateFrom && params.dateTo && Date.parse(params.dateFrom) >= Date.parse(params.dateTo)) {
      setDateRangeError(true);
      return;
    }
    setRows([]);
    setNextCursor(null);
    setResult(undefined);
    void execute(params);
  }

  async function download(format: 'csv' | 'json') {
    if (!selectedReport || !runParams) return;
    try {
      const body = await exportReport({ reportId: selectedReport.id, format, params: runParams }).unwrap();
      const url = URL.createObjectURL(new Blob([body], { type: format === 'csv' ? 'text/csv' : 'application/json' }));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${selectedReport.id}.${format}`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch {
      setRunError(true);
    }
  }

  return (
    <div className="space-y-6">
      <header><h1 className="text-2xl font-bold">Reporting</h1><p className="text-sm text-muted-foreground">Run a registered report, inspect its returned rows, or export CSV/JSON.</p></header>
      <p role="note" className="rounded-md border border-muted-foreground/30 px-4 py-3 text-sm text-muted-foreground">Reports summarize stored order totals, not provider settlement.</p>
      {isLoading ? <LoadingRows label="Loading report descriptors" columns={3} />
        : isError ? <div role="alert" className="rounded-md border border-destructive/40 p-4 text-sm">Could not load report descriptors. <Button variant="outline" onClick={() => void refetch()}>Try again</Button></div>
          : !reports?.length ? <EmptyState title="No reports available" description="No report descriptors are currently returned by the reporting service." />
            : <>
              <form className="max-w-3xl space-y-4" onSubmit={submit}>
                <Field label="Report" id="report-id"><select id="report-id" className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={reportId} onChange={(event) => setReportId(event.target.value)}>{reports.map((report) => <option key={report.id} value={report.id}>{report.name}</option>)}</select></Field>
                {selectedReport && selectedReport.params.length > 0 && <div className="grid gap-4 sm:grid-cols-2">{selectedReport.params.map((field) => {
                  const id = `report-param-${field.name}`;
                  const type = field.type === 'date' ? 'date' : 'text';
                  const exclusive = field.name === 'dateTo';
                  const isDateTo = field.name === 'dateTo';
                  return <Field key={field.name} label={`${field.label}${field.required ? ' (required)' : ' (optional)'}`} id={id}>
                    <Input id={id} type={type} required={field.required} value={values[field.name] ?? ''} aria-invalid={isDateTo && dateRangeError} aria-describedby={isDateTo ? `report-date-exclusive${dateRangeError ? ' report-date-range-error' : ''}` : undefined} onChange={(event) => { setDateRangeError(false); setValues((previous) => ({ ...previous, [field.name]: event.target.value })); }} />
                    {exclusive && <span id="report-date-exclusive" className="text-xs text-muted-foreground">This boundary is exclusive.</span>}
                  </Field>;
                })}</div>}
                {dateRangeError && <p id="report-date-range-error" role="alert" className="text-sm text-destructive">The exclusive end date must be later than the inclusive start date.</p>}
                <div className="flex justify-end"><Button type="submit" disabled={!selectedReport || isRunning}>{isRunning ? 'Running…' : 'Run report'}</Button></div>
              </form>
              {runError && <div role="alert" className="rounded-md border border-destructive/40 p-4 text-sm">Could not run or export this report. Inputs are retained; check the date range and try again.</div>}
              {result && <section className="space-y-4" aria-busy={isRunning}>
                <div className="flex flex-wrap items-center justify-between gap-3"><div><h2 className="text-lg font-semibold">{selectedReport?.name} results</h2><p className="text-sm text-muted-foreground">{rows.length} rows loaded; latest run took {result.durationMs} ms.</p></div><div className="flex gap-2">{selectedReport?.exportFormats.includes('csv') && <Button variant="outline" disabled={isExporting} onClick={() => void download('csv')}>{isExporting ? 'Exporting…' : 'Export CSV'}</Button>}{selectedReport?.exportFormats.includes('json') && <Button variant="outline" disabled={isExporting} onClick={() => void download('json')}>{isExporting ? 'Exporting…' : 'Export JSON'}</Button>}</div></div>
                {rows.length === 0 ? <EmptyState title="No report rows" description="The report ran successfully and returned no rows." />
                  : <div role="region" tabIndex={0} className="overflow-x-auto rounded-md border" aria-label="Report results"><table className="w-full min-w-[36rem] text-sm"><thead className="bg-muted/50 text-left"><tr>{selectedReport?.fields.map((field) => <th key={field.name} scope="col" className={`px-4 py-3 ${field.name.endsWith('Minor') || field.type === 'integer' ? 'text-right' : ''}`}>{field.label}</th>)}</tr></thead><tbody>{rows.map((row, index) => <tr key={`${index}-${String(row.storeId ?? '')}`} className="border-t">{selectedReport?.fields.map((field) => <td key={field.name} className={`px-4 py-3 ${field.name.endsWith('Minor') || field.type === 'integer' ? 'text-right tabular-nums' : ''}`}>{formatReportValue(field.name, row[field.name], row.currency)}</td>)}</tr>)}</tbody></table></div>}
                {nextCursor && <Button variant="outline" disabled={isRunning} onClick={() => void execute(runParams, nextCursor)}>{isRunning ? 'Loading…' : 'Load more report rows'}</Button>}
              </section>}
            </>}
    </div>
  );
}

function formatReportValue(fieldName: string, value: unknown, currency: unknown): string {
  if (value === null || value === undefined) return '—';
  if (fieldName.endsWith('Minor') && typeof value === 'string' && typeof currency === 'string') {
    return formatMinorAmount(value, currency);
  }
  return String(value);
}

function Field({ label, id, children }: { label: string; id: string; children: React.ReactNode }) {
  return <div className="space-y-2"><Label htmlFor={id}>{label}</Label>{children}</div>;
}
